import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Play, Pause, SkipForward, SkipBack, Maximize2, Loader2, Sparkles,
  ChevronRight, ChevronLeft, X, RefreshCw, CheckCircle, AlertCircle, Film, ArrowLeft,
  Share2, QrCode, Copy, Check, Mic, Download
} from "lucide-react";
import { getAuthHeaders } from "../lib/auth";
import { Button } from "../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;

export default function TrainingVideosPage() {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeServiceId, setActiveServiceId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/superadmin/training-videos/services`, { headers: getHeaders() });
      setServices(res.data.services || []);
    } catch (e) {
      toast.error("Accès refusé ou erreur chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll progress every 4s while any service is generating
  useEffect(() => {
    const anyGenerating = services.some(s => s.generating_count > 0 || (s.ready_count < s.scene_count));
    if (!anyGenerating) return;
    const interval = setInterval(load, 4000);
    return () => clearInterval(interval);
  }, [services, load]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white" data-testid="training-videos-page">
      {/* Header */}
      <div className="sticky top-0 z-30 backdrop-blur-lg bg-slate-950/80 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/superadmin")} className="p-2 rounded-lg hover:bg-white/5 text-slate-400" data-testid="back-to-superadmin">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Film className="w-5 h-5 text-amber-400" />
                Centre de formation vidéos
              </h1>
              <p className="text-xs text-slate-400">Outils commerciaux pour le démarchage des pharmacies — 3 mini-films de 75-80 sec</p>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-amber-400" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="services-grid">
            {services.map(svc => (
              <ServiceCard key={svc.id} svc={svc} onWatch={() => setActiveServiceId(svc.id)} onRefresh={load} />
            ))}
          </div>
        )}
      </div>

      {activeServiceId && (
        <VideoPlayerModal
          serviceId={activeServiceId}
          onClose={() => { setActiveServiceId(null); load(); }}
        />
      )}
    </div>
  );
}

function ServiceCard({ svc, onWatch, onRefresh }) {
  const [triggering, setTriggering] = useState(false);
  const [triggeringAudio, setTriggeringAudio] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const progress = svc.scene_count > 0 ? Math.round((svc.ready_count / svc.scene_count) * 100) : 0;
  const audioProgress = svc.scene_count > 0 ? Math.round(((svc.audio_ready_count || 0) / svc.scene_count) * 100) : 0;
  const allReady = svc.ready_count === svc.scene_count;
  const allAudioReady = (svc.audio_ready_count || 0) === svc.scene_count;

  const triggerGen = async (force) => {
    setTriggering(true);
    try {
      await axios.post(`${API}/superadmin/training-videos/generate/${svc.id}`,
        null, { headers: getHeaders(), params: { force } });
      toast.success(`Génération démarrée pour ${svc.title}`);
      onRefresh();
    } catch (e) {
      toast.error("Erreur démarrage génération");
    } finally {
      setTriggering(false);
    }
  };

  const triggerAudioGen = async (force) => {
    setTriggeringAudio(true);
    try {
      await axios.post(`${API}/superadmin/training-videos/generate-audio/${svc.id}`,
        null, { headers: getHeaders(), params: { force, voice: "nova" } });
      toast.success(`Génération voix off démarrée pour ${svc.title} (~30-45 sec)`);
      onRefresh();
    } catch (e) {
      toast.error("Erreur démarrage génération voix off");
    } finally {
      setTriggeringAudio(false);
    }
  };

  const downloadKit = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/superadmin/training-videos/download/${svc.id}`, {
        headers: getHeaders(), responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cmes-video-${svc.id}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Kit ${svc.title} téléchargé`);
    } catch (e) {
      toast.error("Erreur téléchargement du kit");
    } finally {
      setDownloading(false);
    }
  };

  const themeColor = svc.color_theme || "#1e40af";

  return (
    <div className="group relative bg-slate-900/60 border border-white/10 rounded-2xl overflow-hidden hover:border-amber-500/40 transition-all"
      data-testid={`service-card-${svc.id}`}>
      <div className="absolute top-0 left-0 right-0 h-1" style={{ background: themeColor }} />
      <div className="p-6">
        <h3 className="text-lg font-bold text-white mb-1">{svc.title}</h3>
        <p className="text-xs text-slate-400 mb-3">{svc.subtitle}</p>
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 mb-4">
          <p className="text-[11px] text-amber-300 font-semibold">{svc.tagline}</p>
        </div>

        {/* Progress images */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] text-slate-500 uppercase tracking-wider">Images IA</span>
            <span className="text-xs font-mono text-slate-300">{svc.ready_count} / {svc.scene_count}</span>
          </div>
          <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all duration-700"
              style={{ width: `${progress}%` }} />
          </div>
          {svc.generating_count > 0 && (
            <p className="text-[10px] text-amber-400 mt-1.5 flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> {svc.generating_count} en cours...
            </p>
          )}
        </div>

        {/* Progress audio (voix off) */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] text-slate-500 uppercase tracking-wider flex items-center gap-1"><Mic className="w-3 h-3" /> Voix off</span>
            <span className="text-xs font-mono text-slate-300">{svc.audio_ready_count || 0} / {svc.scene_count}</span>
          </div>
          <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-700"
              style={{ width: `${audioProgress}%` }} />
          </div>
          {svc.audio_generating_count > 0 && (
            <p className="text-[10px] text-emerald-400 mt-1.5 flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> {svc.audio_generating_count} narration(s) en cours...
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 flex-wrap">
          {allReady ? (
            <Button onClick={onWatch} className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-slate-950 font-bold gap-2 min-w-[140px]" data-testid={`watch-${svc.id}`}>
              <Play className="w-4 h-4 fill-current" /> Visionner
            </Button>
          ) : (
            <Button onClick={() => triggerGen(false)} disabled={triggering || svc.generating_count > 0}
              className="flex-1 bg-blue-600 hover:bg-blue-500 text-white gap-2 min-w-[140px]" data-testid={`generate-${svc.id}`}>
              {triggering || svc.generating_count > 0 ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {svc.generating_count > 0 ? "En cours..." : "Générer les visuels IA"}
            </Button>
          )}
          {allReady && !allAudioReady && (
            <Button onClick={() => triggerAudioGen(false)} disabled={triggeringAudio || svc.audio_generating_count > 0}
              className="bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5" data-testid={`audio-gen-${svc.id}`} title="Générer la voix off (TTS)">
              {triggeringAudio || svc.audio_generating_count > 0 ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />}
              {svc.audio_generating_count > 0 ? "..." : "Voix off"}
            </Button>
          )}
          {allReady && (
            <>
              <Button onClick={downloadKit} disabled={downloading} variant="outline"
                className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 gap-1.5" data-testid={`download-${svc.id}`} title="Télécharger le kit (images + voix off)">
                {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              </Button>
              <Button onClick={() => setShowShare(true)} variant="outline"
                className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 gap-1.5" data-testid={`share-${svc.id}`} title="Partager">
                <Share2 className="w-4 h-4" />
              </Button>
              <Button onClick={() => triggerGen(true)} disabled={triggering} variant="outline"
                className="border-white/10 text-slate-400 hover:bg-white/5" title="Régénérer les visuels">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </div>
      {showShare && <ShareModal svc={svc} onClose={() => setShowShare(false)} />}
    </div>
  );
}

function ShareModal({ svc, onClose }) {
  const [publicUrl, setPublicUrl] = useState("");
  const [qrBlobUrl, setQrBlobUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let blobUrlLocal = null;
    (async () => {
      try {
        const [urlRes, qrRes] = await Promise.all([
          axios.get(`${API}/superadmin/training-videos/public-url/${svc.id}`, { headers: getHeaders() }),
          axios.get(`${API}/superadmin/training-videos/qr/${svc.id}`, { headers: getHeaders(), responseType: "blob" }),
        ]);
        setPublicUrl(urlRes.data.public_url);
        blobUrlLocal = URL.createObjectURL(qrRes.data);
        setQrBlobUrl(blobUrlLocal);
      } catch {
        setPublicUrl(`${window.location.origin}/v/${svc.id}`);
        toast.error("Erreur chargement du QR");
      }
    })();
    return () => { if (blobUrlLocal) URL.revokeObjectURL(blobUrlLocal); };
  }, [svc.id]);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success("Lien copié");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Erreur de copie");
    }
  };

  const downloadQR = () => {
    if (!qrBlobUrl) return;
    const a = document.createElement("a");
    a.href = qrBlobUrl;
    a.download = `cmes-qr-${svc.id}.png`;
    a.click();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={onClose} data-testid="share-modal">
      <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Share2 className="w-5 h-5 text-amber-400" /> Partager la vidéo
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5 text-slate-400">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-xs text-slate-400 mb-5">
          Liens publics 100% accessibles — utilisez-les en démarchage commercial sans login. Affichez le QR sur tablette ou imprimez-le sur vos cartes de visite.
        </p>

        {/* QR */}
        <div className="bg-white rounded-xl p-4 mb-4 flex justify-center items-center min-h-[200px]" data-testid="share-qr-container">
          {qrBlobUrl ? (
            <img src={qrBlobUrl} alt="QR Code" className="w-48 h-48 object-contain" />
          ) : (
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          )}
        </div>

        {/* URL */}
        <div className="mb-4">
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">Lien public</label>
          <div className="flex gap-2">
            <input value={publicUrl} readOnly
              className="flex-1 px-3 py-2 bg-slate-950 border border-white/10 rounded-lg text-white text-xs font-mono"
              data-testid="share-public-url" />
            <Button onClick={copyUrl} className="bg-amber-500 hover:bg-amber-400 text-slate-950 px-3" data-testid="share-copy-btn">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-2">
          <Button onClick={downloadQR} disabled={!qrBlobUrl} className="bg-slate-800 hover:bg-slate-700 text-white gap-1.5 text-xs" data-testid="share-download-qr">
            <QrCode className="w-4 h-4" /> Télécharger QR
          </Button>
          <Button onClick={() => window.open(publicUrl, "_blank")} variant="outline"
            className="border-white/10 text-slate-300 hover:bg-white/5 text-xs">
            Ouvrir l'aperçu
          </Button>
        </div>
      </div>
    </div>
  );
}

function VideoPlayerModal({ serviceId, onClose }) {
  const [storyboard, setStoryboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [finished, setFinished] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/superadmin/training-videos/storyboard/${serviceId}`, { headers: getHeaders() });
        setStoryboard(res.data);
      } catch (e) {
        toast.error("Erreur chargement storyboard");
        onClose();
      } finally {
        setLoading(false);
      }
    })();
  }, [serviceId, onClose]);

  // Auto-advance
  useEffect(() => {
    if (!storyboard || !playing || finished) return;
    const current = storyboard.scenes[currentIdx];
    if (!current) return;
    timerRef.current = setTimeout(() => {
      if (currentIdx < storyboard.scenes.length - 1) {
        setCurrentIdx(i => i + 1);
      } else {
        setFinished(true);
        setPlaying(false);
      }
    }, current.duration_ms || 5500);
    return () => clearTimeout(timerRef.current);
  }, [storyboard, currentIdx, playing, finished]);

  const next = () => {
    if (!storyboard) return;
    if (currentIdx < storyboard.scenes.length - 1) {
      setCurrentIdx(i => i + 1);
      setFinished(false);
    }
  };
  const prev = () => {
    if (currentIdx > 0) {
      setCurrentIdx(i => i - 1);
      setFinished(false);
    }
  };
  const restart = () => {
    setCurrentIdx(0);
    setFinished(false);
    setPlaying(true);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-amber-400" />
      </div>
    );
  }
  if (!storyboard) return null;

  const scene = storyboard.scenes[currentIdx];
  const progress = ((currentIdx + 1) / storyboard.scenes.length) * 100;
  const themeColor = storyboard.color_theme;

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="video-player-modal">
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-black/80 to-transparent">
        <div className="flex items-center justify-between px-6 py-4">
          <div>
            <p className="text-[10px] text-slate-400 uppercase tracking-widest">{storyboard.title}</p>
            <p className="text-sm text-white font-semibold">{storyboard.tagline}</p>
          </div>
          <button onClick={onClose} className="text-white hover:bg-white/10 p-2 rounded-full" data-testid="player-close">
            <X className="w-6 h-6" />
          </button>
        </div>
        {/* Progress bar */}
        <div className="px-6 pb-3">
          <div className="h-1 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full transition-all duration-300" style={{ width: `${progress}%`, background: themeColor }} />
          </div>
          <div className="flex justify-between text-[10px] text-slate-500 mt-1 uppercase tracking-wider">
            <span>Scène {currentIdx + 1} / {storyboard.scenes.length}</span>
            <span>{Math.round(progress)}%</span>
          </div>
        </div>
      </div>

      {/* Scene viewport */}
      <div className="flex-1 relative overflow-hidden">
        {finished ? (
          <FinalCta storyboard={storyboard} onRestart={restart} onClose={onClose} />
        ) : (
          <SceneFrame key={scene.id} serviceId={serviceId} scene={scene} themeColor={themeColor} />
        )}
      </div>

      {/* Bottom controls */}
      <div className="absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-black/90 to-transparent pt-12 pb-6 px-6">
        <div className="flex items-center justify-center gap-4">
          <button onClick={prev} disabled={currentIdx === 0} className="text-white hover:bg-white/10 p-3 rounded-full disabled:opacity-30 disabled:cursor-not-allowed" data-testid="player-prev">
            <SkipBack className="w-5 h-5" />
          </button>
          <button onClick={() => setPlaying(p => !p)} className="text-slate-950 bg-white hover:bg-amber-300 p-4 rounded-full shadow-lg" data-testid="player-play-pause">
            {playing ? <Pause className="w-6 h-6 fill-current" /> : <Play className="w-6 h-6 fill-current" />}
          </button>
          <button onClick={next} disabled={finished} className="text-white hover:bg-white/10 p-3 rounded-full disabled:opacity-30 disabled:cursor-not-allowed" data-testid="player-next">
            <SkipForward className="w-5 h-5" />
          </button>
        </div>
        {/* Scene dots */}
        <div className="flex items-center justify-center gap-1.5 mt-4">
          {storyboard.scenes.map((s, i) => (
            <button key={s.id} onClick={() => { setCurrentIdx(i); setFinished(false); }}
              className={`h-1.5 rounded-full transition-all ${i === currentIdx ? "w-8 bg-white" : "w-1.5 bg-white/30 hover:bg-white/50"}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

function SceneFrame({ serviceId, scene, themeColor }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center animate-scene-enter">
      <img
        src={`${API}/superadmin/training-videos/image/${serviceId}/${scene.id}`}
        alt={scene.title}
        className="absolute inset-0 w-full h-full object-cover animate-ken-burns"
        loading="eager"
      />
      {/* Vignette overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/80" />
      {/* Side gradient for readability */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-transparent" />

      {/* Text content (bottom-left) */}
      <div className="absolute bottom-32 left-8 md:left-16 max-w-2xl z-10 animate-text-slide-up">
        <div className="inline-block mb-3 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest" style={{ background: themeColor, color: "white" }}>
          {scene.id.replace("scene_", "Scène ")}
        </div>
        <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight" style={{ textShadow: "0 4px 20px rgba(0,0,0,0.8)" }}>
          {scene.title}
        </h2>
        <p className="text-lg md:text-xl text-slate-100 leading-relaxed max-w-xl" style={{ textShadow: "0 2px 10px rgba(0,0,0,0.8)" }}>
          {scene.subtitle}
        </p>
      </div>

      <style>{`
        @keyframes ken-burns {
          0% { transform: scale(1) translate(0, 0); }
          100% { transform: scale(1.08) translate(-1%, -1%); }
        }
        .animate-ken-burns { animation: ken-burns ${(scene.duration_ms || 5500) + 500}ms ease-out forwards; }
        @keyframes scene-enter {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-scene-enter { animation: scene-enter 600ms ease-out; }
        @keyframes text-slide-up {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-text-slide-up { animation: text-slide-up 700ms cubic-bezier(0.16, 1, 0.3, 1) 200ms backwards; }
      `}</style>
    </div>
  );
}

function FinalCta({ storyboard, onRestart, onClose }) {
  const themeColor = storyboard.color_theme;
  return (
    <div className="absolute inset-0 flex items-center justify-center p-8 animate-scene-enter"
      style={{ background: `radial-gradient(ellipse at center, ${themeColor}30 0%, #000 70%)` }}>
      <div className="text-center max-w-3xl">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
          style={{ background: themeColor }}>
          <CheckCircle className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 animate-text-slide-up">
          {storyboard.cta.headline}
        </h2>
        <p className="text-xl text-slate-200 mb-10 animate-text-slide-up" style={{ animationDelay: "200ms" }}>
          {storyboard.cta.body}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-4 animate-text-slide-up" style={{ animationDelay: "400ms" }}>
          <Button onClick={onClose} className="text-slate-950 font-bold px-8 py-6 text-base gap-2 hover:scale-105 transition-transform"
            style={{ background: themeColor, color: "white" }} data-testid="cta-primary">
            {storyboard.cta.button} <ChevronRight className="w-5 h-5" />
          </Button>
          <Button onClick={onRestart} variant="outline" className="border-white/30 text-white hover:bg-white/10 px-6 py-6 gap-2">
            <RefreshCw className="w-4 h-4" /> Revoir
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-10 uppercase tracking-widest">
          CMES Pharma Intel — {storyboard.title}
        </p>
      </div>
    </div>
  );
}
